package ai.beakman.undercover.web

import ai.beakman.undercover.i18n.I18n
import ai.beakman.undercover.i18n.Language
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private enum class Mode { HOME, LOCAL, ONLINE }

/**
 * Raiz do app web: header + roteador de modo.
 * Com deep-link (`initialRoomCode != null`) entra direto no modo Online.
 *
 * Coleta [I18n.language] aqui para que QUALQUER troca de idioma recomponha toda a árvore
 * (todos os textos são lidos de `lang.strings()` / `I18n.strings`).
 */
@Composable
fun App(initialRoomCode: String?) {
    // Coletar o idioma aqui faz TODA a árvore recompor ao trocar de idioma; os filhos
    // leem `I18n.strings` / `lang.strings()` já no idioma novo.
    val lang by I18n.language.collectAsState()

    var pendingCode by remember { mutableStateOf(initialRoomCode) }
    var mode by remember { mutableStateOf(if (initialRoomCode != null) Mode.ONLINE else Mode.HOME) }

    Div({ classes("screen") }) {
        // `key(lang)` recria a subárvore ao trocar de idioma: como os textos são lidos de
        // `I18n.strings` (global, sem snapshot), os filhos parametrizados seriam pulados pelo
        // skipping do Compose; recriar sob a key garante que TODOS os textos reflitam o idioma.
        key(lang) {
            Header()
            when (mode) {
                Mode.HOME -> HomeScreen(
                    onLocal = { mode = Mode.LOCAL },
                    onOnline = { mode = Mode.ONLINE },
                )
                Mode.LOCAL -> LocalFlow(onExit = { mode = Mode.HOME })
                Mode.ONLINE -> OnlineFlow(
                    initialCode = pendingCode,
                    onExit = {
                        pendingCode = null // deep-link só vale na primeira entrada
                        mode = Mode.HOME
                    },
                )
            }
        }
    }
}

/** Home: seletor de idioma + escolha de modo (espelha o ModeScreen do shared/ui/UndercoverApp.kt). */
@Composable
private fun HomeScreen(onLocal: () -> Unit, onOnline: () -> Unit) {
    val s = I18n.strings
    Div({ classes("screen-body", "center") }) {
        LanguageSelector()
        // Caminho ABSOLUTO: sob deep-link /sala/CODIGO o rewrite SPA quebraria src relativo.
        Img(src = "/undercover_home.jpg", alt = s.webHomeHeroAlt) { classes("home-hero") }
        Div({
            classes("muted", "text-center")
            style { property("margin-bottom", "8px") }
        }) { Text(s.homeHowToPlay) }
        ModeCard("📱", s.modeLocalTitle, s.modeLocalSubtitle, onLocal)
        ModeCard("🌐", s.modeOnlineTitle, s.modeOnlineSubtitleWeb, onOnline)
    }
}

/** Seletor compacto de idioma EN/PT/ES; destaca o atual e troca via I18n.setLanguage. */
@Composable
private fun LanguageSelector() {
    val current by I18n.language.collectAsState()
    Div({
        classes("lang-selector")
        style {
            property("display", "flex")
            property("gap", "6px")
            property("justify-content", "center")
            property("margin-bottom", "12px")
        }
    }) {
        Language.entries.forEach { lang ->
            val selected = lang == current
            Button({
                classes("lang-chip")
                if (selected) classes("selected")
                style {
                    property("padding", "5px 12px")
                    property("border-radius", "var(--radius-pill)")
                    property("border", if (selected) "1px solid var(--primary)" else "1px solid var(--border)")
                    property("background", if (selected) "var(--primary)" else "var(--secondary)")
                    property("color", if (selected) "var(--on-primary)" else "var(--muted)")
                    property("font-size", "13px")
                    property("font-weight", if (selected) "700" else "500")
                    property("font-family", "inherit")
                    property("cursor", "pointer")
                }
                onClick { I18n.setLanguage(lang) }
            }) { Text(lang.code.uppercase()) }
        }
    }
}

@Composable
private fun ModeCard(emoji: String, title: String, subtitle: String, onClick: () -> Unit) {
    Button({
        classes("mode-card")
        onClick { onClick() }
    }) {
        Span({ classes("mode-emoji") }) { Text(emoji) }
        Div {
            Div({ classes("mode-title") }) { Text(title) }
            Div({ classes("mode-subtitle") }) { Text(subtitle) }
        }
    }
}
